"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
function NewArrivalsGrid() {
  const t = useT();
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
        {[...Array(12)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-square w-full animate-pulse rounded-md bg-card/50 border border-border/50"></div>
            <div className="h-6 w-3/4 animate-pulse rounded bg-card/50 mx-auto"></div>
            <div className="h-4 w-1/4 animate-pulse rounded bg-card/50 mx-auto"></div>
            <div className="mt-4 h-10 w-full animate-pulse rounded-md bg-card/50"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        <p>{t("Unable to synchronize laboratory inventory at this time.")}</p>
      </div>;
  }
  if (!products || products.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
      {products.map((product, i) => <div key={product.id} data-index={i} className="group relative flex flex-col overflow-hidden rounded-md border border-white/10 bg-card/40 p-4 backdrop-blur-md transition-all duration-300 hover:border-primary/50 hover:shadow-[0_0_20px_rgba(0,102,255,0.15)]">
          {}
          <div className="pointer-events-none absolute inset-0 z-10 h-[2px] w-full -translate-y-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 transition-all duration-[1500ms] ease-in-out group-hover:translate-y-[400px] group-hover:opacity-100" />
          
          <Link to={product.link} className="relative mb-6 block aspect-square overflow-hidden rounded-md bg-secondary/50">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/30">
                <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-card to-primary/10">
                  <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,102,255,0.15) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}></div>
                  <div className="flex h-full w-full items-center justify-center">
                    <svg className="h-12 w-12 text-primary/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4.5 3h15" />
                      <path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3" />
                      <path d="M6 14h12" />
                    </svg>
                  </div>
                </div>
              </div>}
          </Link>

          <Link to={product.link} className="mb-2 block text-center">
            <h3 className="font-default text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
              {product.title}
            </h3>
          </Link>

          <div className="mb-6 text-center font-mono text-sm font-medium text-primary">
            {(product.price ?? 0).toFixed(2)} {product.currency_code || "USD"}
          </div>

          <div className="mt-auto opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <AddToCartButton productId={product.id} variant="outline" className="w-full" buttonClassName="w-full rounded-md border border-primary bg-primary/10 py-6 font-default text-xs font-bold uppercase tracking-widest text-primary transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-[0_0_15px_rgba(0,102,255,0.4)]" text={t("Add to Cart")} loadingText={t("Adding...")} showQuantity={false} />
          </div>
        </div>)}
    </div>;
}
export default function NewArrivals() {
  const t = useT();
  const wp_query_params = {
    per_page: 12,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="223061" id="elite-formulations" className="bg-background text-foreground py-16 md:py-24 mt-20">
      <div className="mx-auto max-w-7xl px-8 md:px-12">
        <div className="mb-24 flex items-center justify-between">
          <div>
            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground">
              {t("Our Products")}
            </h2>
            <p className="mt-2 font-default text-sm tracking-tight text-muted-foreground text-red-500">
              {t("All products sold on this website are intended for research and identification purposes only. These products are not intended for human dosing, injection or ingestion.")}
            </p>
          </div>
          <Link to="/ShopPage" className="text-blue-500 font-semibold hover:text-blue-400 underline transition-colors">
            {t("View All")}
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 12,
        orderby: "date",
        order: "desc"
      }}>
          <NewArrivalsGrid />
        </EcommerceProductsProvider>

        <div className="mt-16 flex justify-center">
          <Button asChild variant="default" size="lg" className="rounded-md bg-primary px-8 py-6 font-default text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:shadow-[0_0_20px_rgba(0,102,255,0.4)]">
            <Link to="/ShopPage">{t("Shop more")}</Link>
          </Button>
        </div>
      </div>
    </section>;
}